import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createEditor, Editor, Range, Transforms } from "slate";
import { Editable, ReactEditor, Slate, useSlate, withReact } from "slate-react";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { AppIcon } from "@toothfairy/shared-ui";
import {
  CHIP_TYPE,
  insertTokenAsChip,
  makeComposerValue,
  serializeSlateToText,
  slatePointToStringOffset,
  stringOffsetToSlatePoint,
} from "../../../Utility/clipboardUtils";

// Inline-void plugin: chip nodes are atomic (deleted as one unit, not
// editable inside) and flow inline with the text.
const withChips = (editor) => {
  const { isInline, isVoid, insertData, insertBreak } = editor;
  editor.isInline = (element) =>
    element.type === CHIP_TYPE ? true : isInline(element);
  editor.isVoid = (element) =>
    element.type === CHIP_TYPE ? true : isVoid(element);
  // Single-block composer: Enter (when not intercepted for send) is a soft
  // line break, and pasted clipboard text inserts as plain text.
  editor.insertBreak = () => editor.insertText("\n");
  editor.insertData = (data) => {
    const text = data.getData("text/plain");
    if (text) editor.insertText(text);
  };
  return editor;
};

const CHIP_ICONS = {
  clipboard: AppAssets?.icons?.document,
  image: AppAssets?.icons?.image,
  tool: AppAssets?.icons?.bolt,
  agent: AppAssets?.icons?.agent,
};

const ChipElement = ({ attributes, children, element, theme, labelMap }) => {
  const editor = useSlate();
  // Agent pills use the orange accent (matching the old delegation chip),
  // everything else uses the primary accent.
  const chipColor =
    element.chipKind === "agent"
      ? theme?.orange || theme?.primary
      : theme?.primary;
  const chipBackground =
    element.chipKind === "agent" && theme?.orange
      ? `${theme.orange}1A`
      : theme?.primary_soft;

  const handleRemove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      const path = ReactEditor.findPath(editor, element);
      Transforms.removeNodes(editor, { at: path });
      ReactEditor.focus(editor);
    } catch (e) {
      // element already gone
    }
  };

  return (
    <span
      {...attributes}
      contentEditable={false}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 8px",
        margin: "0 1px",
        verticalAlign: "middle",
        borderRadius: 7,
        border: `1.5px solid ${chipColor}`,
        backgroundColor: chipBackground,
        color: chipColor,
        fontSize: theme?.extra_small_font_size,
        fontWeight: 600,
        letterSpacing: "0.2px",
        lineHeight: "18px",
        userSelect: "none",
      }}
    >
      {CHIP_ICONS[element.chipKind] ? (
        <AppIcon
          icon={CHIP_ICONS[element.chipKind]}
          size={11}
          color={chipColor}
          wrapperStyle={{ marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 }}
        />
      ) : null}
      {labelMap?.[element.token] || element.label}
      <span
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleRemove}
        style={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
          opacity: 0.65,
          marginLeft: 2,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.65)}
      >
        <AppIcon
          icon={AppAssets?.icons?.close}
          size={9}
          color={chipColor}
          wrapperStyle={{ marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 }}
        />
      </span>
      {children}
    </span>
  );
};

/**
 * First-class chip composer for the chat input: a minimal single-block Slate
 * editor where tokens (<clipboard 1>, <image 1>, ~tool_id~) render as pills.
 * The string contract is preserved end to end: parents read/write plain text
 * with tokens via the imperative facade, exactly like a <textarea>.
 */
const TokenComposerInput = forwardRef(
  (
    {
      defaultValue = "",
      placeholder = "",
      disabled = false,
      opacity = 1,
      currentTheme,
      onChange = () => {},
      onKeyDown = () => {},
      onPaste = () => {},
      labelMap = null,
      agentLabels = [],
      style = {},
    },
    ref,
  ) => {
    const theme = currentTheme?.theme || {};
    const editor = useMemo(() => withChips(withReact(createEditor())), []);
    const editableRef = useRef(null);
    // Controlled Slate value (the installed slate-react uses the controlled
    // <Slate value onChange> API, like AppQuantumEditor)
    const [slateValue, setSlateValue] = useState(() =>
      makeComposerValue(defaultValue || "", agentLabels),
    );

    const serialize = useCallback(
      () => serializeSlateToText(editor.children),
      [editor],
    );

    // The scrollable DOM node of the editor (Editable's ref is not the div in
    // this slate-react version)
    const getEditableElement = useCallback(() => {
      try {
        return ReactEditor.toDOMNode(editor, editor);
      } catch (e) {
        return editableRef.current;
      }
    }, [editor]);

    useImperativeHandle(ref, () => ({
      get value() {
        return serialize();
      },
      set value(next) {
        const caretOffset = serialize().length;
        editor.children = makeComposerValue(next || "", agentLabels);
        editor.selection = null;
        Editor.normalize(editor, { force: true });
        editor.onChange();
        try {
          Transforms.select(
            editor,
            stringOffsetToSlatePoint(editor, caretOffset),
          );
        } catch (e) {
          // selection beyond document end - ignore
        }
      },
      focus() {
        try {
          if (!editor.selection) {
            Transforms.select(editor, Editor.end(editor, []));
          }
          ReactEditor.focus(editor);
        } catch (e) {
          // editor not mounted yet
        }
      },
      blur() {
        try {
          ReactEditor.blur(editor);
        } catch (e) {
          // editor not mounted yet
        }
      },
      setSelectionRange(start, end) {
        Transforms.select(editor, {
          anchor: stringOffsetToSlatePoint(editor, start),
          focus: stringOffsetToSlatePoint(editor, end ?? start),
        });
      },
      get selectionStart() {
        if (!editor.selection) return serialize().length;
        return slatePointToStringOffset(editor, Range.start(editor.selection));
      },
      get selectionEnd() {
        if (!editor.selection) return serialize().length;
        return slatePointToStringOffset(editor, Range.end(editor.selection));
      },
      insertToken(token) {
        insertTokenAsChip(editor, token);
        this.focus();
      },
      // Scroll plumbing used by the parents' scroll effects - proxies to the
      // editable div so those effects keep working unchanged.
      get scrollTop() {
        return getEditableElement()?.scrollTop ?? 0;
      },
      set scrollTop(value) {
        const el = getEditableElement();
        if (el) el.scrollTop = value;
      },
      get scrollHeight() {
        return getEditableElement()?.scrollHeight ?? 0;
      },
      addEventListener: (...args) =>
        getEditableElement()?.addEventListener(...args),
      removeEventListener: (...args) =>
        getEditableElement()?.removeEventListener(...args),
      getBoundingClientRect: () => getEditableElement()?.getBoundingClientRect(),
    }));

    const handleChange = useCallback(
      (nextValue) => {
        const text = serializeSlateToText(nextValue);
        const selectionStart = editor.selection
          ? slatePointToStringOffset(editor, Range.start(editor.selection))
          : text.length;
        onChange({ text, selectionStart });
      },
      [editor, onChange],
    );

    const handleKeyDown = useCallback(
      (event) => {
        onKeyDown(event);
      },
      [onKeyDown],
    );

    const handlePaste = useCallback(
      (event) => {
        onPaste(event);
      },
      [onPaste],
    );

    const handleCopy = useCallback(
      (event) => {
        if (!editor.selection || Range.isCollapsed(editor.selection)) return;
        event.preventDefault();
        const from = slatePointToStringOffset(
          editor,
          Range.start(editor.selection),
        );
        const to = slatePointToStringOffset(editor, Range.end(editor.selection));
        const text = serialize().slice(from, to);
        event.clipboardData.setData("text/plain", text);
      },
      [editor, serialize],
    );

    const renderElement = useCallback(
      (props) => {
        if (props.element.type === CHIP_TYPE) {
          return <ChipElement {...props} theme={theme} labelMap={labelMap} />;
        }
        return <p {...props.attributes} style={{ margin: 0 }}>{props.children}</p>;
      },
      [theme, labelMap],
    );

    return (
      <div
        className="tf-token-composer"
        style={{
          flex: 1,
          width: "100%",
          opacity,
          display: "flex",
          // minHeight: 0 + overflow hidden are what bound the composer to its
          // parent card: flex items otherwise floor at content size and the
          // editor grows out of the fixed-height container instead of
          // scrolling internally.
          minHeight: 0,
          overflow: "hidden",
          ...style,
        }}
      >
        <style>
          {`.tf-token-composer [data-slate-placeholder] { color: ${
            theme?.mediumGrey || "#888"
          } !important; opacity: 1 !important; font-style: normal !important; }`}
        </style>
        <Slate editor={editor} value={slateValue} onChange={handleChange}>
          <Editable
            ref={editableRef}
            renderElement={renderElement}
            placeholder={placeholder}
            readOnly={disabled}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onCopy={handleCopy}
            style={{
              flex: 1,
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              minHeight: 0,
              outline: "none",
              fontFamily: "normalFont",
              fontSize: theme?.small_font_size,
              lineHeight: "22px",
              color: theme?.font_color,
              backgroundColor: theme?.transparent,
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "normal",
              overflowY: "auto",
              boxSizing: "border-box",
            }}
          />
        </Slate>
      </div>
    );
  },
);

TokenComposerInput.displayName = "TokenComposerInput";

export default TokenComposerInput;
